import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api/client';
import { useBranches } from '../../hooks/useBranches';
import { usePermission } from '../../hooks/usePermission';
import { useToast } from '../../components/ui/Toast';
import { useKeyboardForm } from '../../hooks/useKeyboardForm';
import { Empty, ErrorNotice, Loading, PageHeader } from '../../components/ui';
import { Field, Select, TextInput } from '../../components/ui/Form';

interface Loc {
  id: string;
  code: string;
  name: string;
  branch_id: string | null;
  is_active: boolean;
  version: number;
}

/**
 * The showcases, trays, safes and strong rooms a shop keeps pieces in.
 *
 * Each shows how many pieces are in it right now, and opens the stock list
 * filtered to it — which is the list to count against when checking a showcase
 * at closing. A location is never deleted, only retired, because every move a
 * piece ever made names the place it went.
 */
export function LocationsPage() {
  const { can } = usePermission();
  const client = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const onEnter = useKeyboardForm();
  const { branches, defaultBranchId } = useBranches();
  const [form, setForm] = useState({ code: '', name: '', branchId: '' });
  const [editing, setEditing] = useState<Loc | null>(null);
  const [error, setError] = useState<unknown>(null);

  const list = useQuery({
    queryKey: ['locations'],
    queryFn: () => api.get<{ data: Loc[] }>('/locations?limit=200'),
  });
  // Counted from the same report a showcase is checked against.
  const counts = useQuery({
    queryKey: ['stock-by-location'],
    queryFn: () => api.get<{ rows?: { location_name: string }[]; data?: { location_name: string }[] }>('/reports/stock-by-location/run'),
  });

  const pieceCount = new Map<string, number>();
  for (const r of counts.data?.rows ?? counts.data?.data ?? []) {
    pieceCount.set(r.location_name, (pieceCount.get(r.location_name) ?? 0) + 1);
  }
  const unplaced = pieceCount.get('— not placed —') ?? 0;

  const create = useMutation({
    mutationFn: () =>
      editing
        ? // The code is printed on tags already hanging in the showcase, so a
          // correction changes the name and branch but leaves the code alone.
          api.patch(
            `/locations/${editing.id}`,
            { name: form.name.trim(), branch_id: form.branchId || editing.branch_id || null },
            editing.version,
          )
        : api.post('/locations', {
            code: form.code.trim().toUpperCase(),
            name: form.name.trim(),
            branch_id: form.branchId || defaultBranchId || null,
          }),
    onSuccess: () => {
      toast(editing ? `${form.name} updated` : `${form.name} added`);
      setEditing(null);
      setForm({ code: '', name: '', branchId: form.branchId });
      setError(null);
      void client.invalidateQueries({ queryKey: ['locations'] });
    },
    onError: setError,
  });

  const toggle = useMutation({
    mutationFn: (l: Loc) => api.patch(`/locations/${l.id}`, { is_active: !l.is_active }, l.version),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['locations'] }),
    onError: setError,
  });

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    if (form.code.trim() && form.name.trim()) create.mutate();
  };

  const rows = list.data?.data ?? [];
  const branchName = (id: string | null): string => (id ? branches.find((b) => b.id === id)?.name ?? '—' : 'Any branch');

  return (
    <>
      <PageHeader
        title="Locations"
        subtitle="Showcases, trays and safes — so every piece has a place and a missing one is noticed."
      />
      <ErrorNotice error={error} />

      {unplaced > 0 ? (
        <button
          className="mb-4 w-full rounded-md border border-warn/30 bg-warn/10 px-3 py-2 text-left text-sm"
          onClick={() => navigate('/items')}
        >
          <span className="font-semibold">{unplaced}</span> piece{unplaced === 1 ? ' is' : 's are'} in stock with no place
          set. Open Stock and give each one a location.
        </button>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div>
          {list.isLoading ? <Loading rows={4} /> : null}
          {!list.isLoading && rows.length === 0 ? (
            <Empty
              message="No locations yet."
              hint="Add each showcase, tray and safe. Purchases can then put pieces straight into them, and the stock list can be checked one showcase at a time."
            />
          ) : null}

          {rows.length > 0 ? (
            <div className="grid gap-3 sm:grid-cols-2">
              {rows.map((l) => (
                <div
                  key={l.id}
                  className={`rounded-xl border bg-surface p-4 shadow-card ${l.is_active ? 'border-silver-100' : 'border-dashed border-silver-400 opacity-60'}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold">{l.name}</p>
                      <p className="num text-2xs text-ink-faint">
                        {l.code} · {branchName(l.branch_id)}
                      </p>
                    </div>
                    <p className="num text-2xl font-bold text-gold-700">{pieceCount.get(l.name) ?? 0}</p>
                  </div>
                  <div className="mt-3 flex items-center gap-3 text-2xs">
                    <button className="text-gold-700 underline" onClick={() => navigate(`/items?locationId=${l.id}`)}>
                      See pieces
                    </button>
                    {can('master.manage') && l.is_active ? (
                      <button
                        className="text-gold-700 underline"
                        onClick={() => {
                          setEditing(l);
                          setForm({ code: l.code, name: l.name, branchId: l.branch_id ?? '' });
                        }}
                      >
                        Edit
                      </button>
                    ) : null}
                    {can('document.void') ? (
                      <button className="text-ink-soft underline" onClick={() => toggle.mutate(l)}>
                        {l.is_active ? 'Retire' : 'Bring back'}
                      </button>
                    ) : null}
                    {!l.is_active ? <span className="text-ink-faint">retired</span> : null}
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </div>

        <form autoComplete="off" onKeyDown={onEnter} onSubmit={submit} className="card h-fit space-y-3">
          <h2 className="text-sm font-semibold">{editing ? `Edit “${editing.name}”` : 'Add a location'}</h2>
          <Field label="Name" required>
            <TextInput value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="Showcase 1" maxLength={100} />
          </Field>
          <Field label="Short code" required hint={editing ? 'Stays as it is — it is on the tags already.' : 'Printed on tags and lists.'}>
            <TextInput
              value={form.code}
              onChange={(v) => setForm({ ...form, code: v })}
              placeholder="SC-1"
              maxLength={16}
              uppercase
              allow={/[A-Z0-9\\-_]/}
              disabled={!!editing}
            />
          </Field>
          {branches.length > 1 ? (
            <Field label="Branch">
              <Select
                value={form.branchId || defaultBranchId || ''}
                onChange={(v) => setForm({ ...form, branchId: v })}
                options={branches.map((b) => ({ value: b.id, label: b.name }))}
              />
            </Field>
          ) : null}
          <div className="flex gap-2">
            <button className="btn-primary flex-1" disabled={!form.code.trim() || !form.name.trim() || create.isPending}>
              {create.isPending ? 'Saving…' : editing ? 'Save changes' : 'Add location'}
            </button>
            {editing ? (
              <button
                type="button"
                className="btn-ghost"
                onClick={() => {
                  setEditing(null);
                  setForm({ code: '', name: '', branchId: '' });
                }}
              >
                Cancel
              </button>
            ) : null}
          </div>
        </form>
      </div>
    </>
  );
}
